import React from "react";
import FeatureSettings from "../settings/FeatureSettings";
import { FEATURE_SETTING_EDITORS } from "../settings/featureSettingsCatalog";
import FeatureComparison from "./FeatureComparison";
import FeatureSettingsActions from "./FeatureSettingsActions";
import LocationDetailsEditor from "./LocationDetailsEditor";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import { FeatureAccessBadge } from "./FeatureAccessDialog";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import { PurpleSwitch } from "../../../helpers/helpers";
import LoadingButton from "@mui/lab/LoadingButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import SaveIcon from "@mui/icons-material/Save";
import TextField from "@mui/material/TextField";
import ImportStoresModal from "./ImportStoresModal";
import ApplyDefaultsToLocationsModal from "./ApplyDefaultsToLocationsModal";
import TimeEntriesSettings from "../time_clock/TimeEntriesSettings";
import LiveAlertSettings from "../time_clock/LiveAlertSettings";
import WarningIcon from "@mui/icons-material/Warning";
import {
  ALWAYS_ENABLED_FLAGS,
  FEATURE_ACCESS,
  LOCKED_FLAG_MESSAGES,
  ORDERED_FLAGS,
  getVisibleBuckets,
  formatFlagName,
  getFlagTooltip,
} from "./featureFlagConstants";

const getCompanyEditor = (panel) => panel?.scope === "company"
  ? (FEATURE_SETTING_EDITORS[panel.bucketKey] || []).find((editor) => editor.scope === "company" && (!panel.focusEditorId || editor.id === panel.focusEditorId))
  : undefined;
const isCompanyPage = (panel) => getCompanyEditor(panel)?.presentation === "page";

function ManageLocationsDisplay(props) {
  const [locationData, setLocationData] = React.useState(props.locationsData);
  const [selection, setSelection] = React.useState(null);
  const detailPaper = React.useRef(null);
  const [visitedPanels, setVisitedPanels] = React.useState({});
  const comparisonScrollPosition = React.useRef(0);
  const selectSettings = React.useCallback((next) => {
    const panel = next.scope === "company" ? { ...next, focusEditorId: getCompanyEditor(next)?.id } : next;
    const key = `${panel.scope}:${panel.storeId || "company"}:${panel.bucketKey}${panel.scope === "company" ? `:${panel.focusEditorId}` : ""}`;
    if (isCompanyPage(panel)) comparisonScrollPosition.current = window.scrollY;
    setVisitedPanels((previous) => ({ ...previous, [key]: panel }));
    setSelection({ ...panel, key });
  }, []);
  const showingCompanyPage = isCompanyPage(selection);
  const returnToComparison = () => {
    setSelection(null);
    window.requestAnimationFrame(() => window.scrollTo(0, comparisonScrollPosition.current));
  };
  React.useEffect(() => {
    if (!selection) return;
    const frame = window.requestAnimationFrame(() => {
      if (selection.focusFlag) document.getElementById(`${selection.key}:${selection.focusFlag}`)?.scrollIntoView({ block: "center" });
      else if (selection.focusEditorId && selection.scope !== "company") detailPaper.current?.querySelector(`[data-setting-editor="${selection.focusEditorId}"]`)?.scrollIntoView({ block: "start" });
      else if (selection.focusSurface) detailPaper.current?.querySelector(`[data-config-surface="${selection.focusSurface}"]`)?.scrollIntoView({ block: "start" });
      else if (isCompanyPage(selection)) window.scrollTo(0, 0);
      else if (detailPaper.current) detailPaper.current.scrollTop = 0;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selection]);
  React.useEffect(() => {
    if (props.companySettingsBucket) selectSettings({ scope: "company", bucketKey: props.companySettingsBucket, focusEditorId: props.companySettingsEditor });
  }, [props.companySettingsBucket, props.companySettingsEditor, selectSettings]);
  const focusStoreId = props.focusEditor?.storeId;
  const focusBucket = props.focusEditor?.bucketKey;
  const focusAt = props.focusEditor?.at;
  React.useEffect(() => {
    if (!focusStoreId || !focusAt) return;
    selectSettings({ scope: "location", storeId: focusStoreId, bucketKey: focusBucket || "location_details" });
  }, [focusStoreId, focusBucket, focusAt, selectSettings]);
  const [updatingFlags, setUpdatingFlags] = React.useState({});
  const [copiedSettings, setCopiedSettings] = React.useState(null);
  const [, setUpdatingStoreIds] = React.useState({});
  const updatingStoreIdsRef = React.useRef({});

  // "Company Default" card state — the flag values new locations inherit.
  // Mirrors how locationData is seeded from props.locationsData.
  const [companyFlags, setCompanyFlags] = React.useState(props.companyFlags || {});
  const [updatingCompanyFlags, setUpdatingCompanyFlags] = React.useState({});
  // The just-written default(s) awaiting an apply/dismiss decision, shown in a
  // modal. The default is NOT a live cascade, so pushing to existing locations
  // is an explicit, opt-in step. Shape:
  //   { flagKeys: [flag], source: "toggle" | "paste", sourceName }
  //
  // The modal only opens once the company_only write RESOLVES, so an admin can
  // flip several defaults inside that window. Prompts therefore MERGE rather
  // than replace, or an earlier flag would stay saved as a default with its
  // apply-to-locations choice silently dropped.
  //
  // Only keys are stored: values are read from companyFlags at render/apply
  // time, so a prompt can never push a value the default has since moved past.
  const [applyPrompt, setApplyPrompt] = React.useState(null);
  // True while a card-wide company-default write (paste or its apply) is in
  // flight, to lock the copy/paste/apply controls.
  const [companyBusy, setCompanyBusy] = React.useState(false);

  // A company config panel awaiting its "apply to locations" decision:
  // { surfaceKey, label }. Kept separate from applyPrompt (the flag prompt) so a
  // feature-flag write resolving mid-decision can't overwrite it, or vice versa.
  const [configApplyPrompt, setConfigApplyPrompt] = React.useState(null);

  // True while the open company config panel has an auto-save in flight. The
  // push re-reads the template from the server, so applying before the write
  // lands would copy the pre-edit values out to every location.
  const [savingConfigs, setSavingConfigs] = React.useState({});
  const configSaving = Object.values(savingConfigs).some(Boolean);
  const reportConfigSaving = (surface, busy) => setSavingConfigs((previous) => ({ ...previous, [surface]: busy }));

  React.useEffect(() => {
    setCompanyFlags(props.companyFlags || {});
  }, [props.companyFlags]);

  const orderedFlags = ORDERED_FLAGS;

  // New locations carry no flag values — the backend seeds them from the
  // company's feature defaults (see update_manager_store_rightwork_data).
  const default_data = {
    store_name: "",
    latitude: "",
    longitude: "",
    onboarding_status: "setting_up",
    store_group: null,
    mobile_app_enabled: true
  };

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [modalData, setModalData] = React.useState(default_data);
  const [modalEdit, setModalEdit] = React.useState(false);
  const [modalSaving, setModalSaving] = React.useState(false);
  const isRightworkInternal = props.permissionRole === "rightwork_internal";
  const visibleBuckets = getVisibleBuckets(isRightworkInternal);

  // POS Modal state
  const [posModalOpen, setPosModalOpen] = React.useState(false);

  const handlePosModalOpen = () => setPosModalOpen(true);
  const handlePosModalClose = () => setPosModalOpen(false);

  const getFormattedOnboardingStatus = {
    active: "active",
    inactive: "inactive",
    setting_up: "setting up"
  };

  const applyAlwaysEnabledFlags = (storeData = {}) => {
    const next = { ...storeData };
    ALWAYS_ENABLED_FLAGS.forEach((flag) => {
      next[flag] = true;
    });
    return next;
  };

  React.useEffect(() => {
    setLocationData(props.locationsData?.map((location) => applyAlwaysEnabledFlags(location)));
  }, [props.locationsData]);

  const getLockedFlagMessage = (flag) => LOCKED_FLAG_MESSAGES[flag];

  const isFlagLocked = (flag) =>
    !isRightworkInternal && Boolean(getLockedFlagMessage(flag));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setModalData((prev) => ({ ...prev, [name]: value }));
  };

  // Regions and districts for the drawer's Store group select. A location may
  // sit in either; districts are labelled with their region.
  const groupOptions = React.useMemo(() => {
    const groups = props.storeGroups || [];
    const regionName = (uuid) => groups.find((group) => group.uuid === uuid)?.name;
    const label = (group) => {
      if (group.kind === "region") return `${group.name} (region)`;
      const region = group.parent_uuid ? regionName(group.parent_uuid) : null;
      return region ? `${group.name} · ${region}` : group.name;
    };
    return groups
      .map((group) => ({ value: group.uuid, label: label(group), kind: group.kind }))
      .sort((a, b) => a.kind.localeCompare(b.kind) || a.label.localeCompare(b.label));
  }, [props.storeGroups]);

  const getInlineToggleKey = (location, flag) =>
    `${location?.id || location?.store_name}-${flag}`;

  const isMatchingLocation = (candidate, reference) => {
    if (reference?.id) {
      return candidate?.id === reference.id;
    }

    return candidate?.store_name === reference?.store_name;
  };

  const isFlagUpdating = (location, flag) =>
    Boolean(updatingFlags[getInlineToggleKey(location, flag)]);

  const getStoreUpdateKey = (location) =>
    location?.id || location?.store_name;

  const isStoreUpdating = (location) =>
    Boolean(updatingStoreIdsRef.current[getStoreUpdateKey(location)]);

  const setStoreUpdating = (location, isUpdating) => {
    const key = getStoreUpdateKey(location);
    const next = { ...updatingStoreIdsRef.current };

    if (isUpdating) {
      next[key] = true;
    } else {
      delete next[key];
    }

    updatingStoreIdsRef.current = next;
    setUpdatingStoreIds(next);
  };

  const handleInlineFeatureToggle = async (location, flag) => {
    if (isFlagLocked(flag)) {
      return;
    }

    if (isStoreUpdating(location)) {
      return;
    }

    const toggleKey = getInlineToggleKey(location, flag);
    if (updatingFlags[toggleKey]) {
      return;
    }

    const updatedLocation = applyAlwaysEnabledFlags({
      ...location,
      [flag]: !location[flag]
    });

    setStoreUpdating(location, true);
    setUpdatingFlags((prev) => ({
      ...prev,
      [toggleKey]: true
    }));

    setLocationData((prev) =>
      prev?.map((item) => (isMatchingLocation(item, location) ? updatedLocation : item))
    );

    const updateSucceeded = await props.update_store_info(updatedLocation);

    if (!updateSucceeded) {
      setLocationData((prev) =>
        prev?.map((item) =>
          isMatchingLocation(item, location)
            ? {
                ...item,
                [flag]: location[flag],
              }
            : item
        )
      );
    }

    setUpdatingFlags((prev) => {
      const next = { ...prev };
      delete next[toggleKey];
      return next;
    });
    setStoreUpdating(location, false);
  };

  const copyStoreSettings = (location) => {
    const flags = orderedFlags.reduce((acc, flag) => {
      acc[flag] = location[flag];
      return acc;
    }, {});

    setCopiedSettings({
      sourceStoreId: location.id,
      sourceStoreName: location.store_name,
      flags
    });
  };

  const pasteStoreSettings = async (location) => {
    if (!copiedSettings || copiedSettings.sourceStoreId === location.id || isStoreUpdating(location)) {
      return;
    }

    const pastedFlags = orderedFlags.reduce((acc, flag) => {
      acc[flag] = isFlagLocked(flag) ? location[flag] : copiedSettings.flags[flag];
      return acc;
    }, {});

    const updatedLocation = applyAlwaysEnabledFlags({
      ...location,
      ...pastedFlags
    });

    setStoreUpdating(location, true);
    setLocationData((prev) =>
      prev?.map((item) => (isMatchingLocation(item, location) ? updatedLocation : item))
    );

    if (modalEdit && isMatchingLocation(modalData, location)) {
      setModalData(updatedLocation);
    }

    const updateSucceeded = await props.update_store_info(updatedLocation);

    if (!updateSucceeded) {
      setLocationData((prev) =>
        prev?.map((item) => (isMatchingLocation(item, location) ? location : item))
      );

      if (modalEdit && isMatchingLocation(modalData, location)) {
        setModalData(location);
      }
    }

    setStoreUpdating(location, false);
  };

  const isAlwaysOnFlag = (flag) => ALWAYS_ENABLED_FLAGS.includes(flag);

  const isCompanyFlagUpdating = (flag) => Boolean(updatingCompanyFlags[flag]);

  const companyFlagWriteInFlight = Object.keys(updatingCompanyFlags).length > 0;

  const setCompanyFlagUpdating = (flag, isUpdating) => {
    setUpdatingCompanyFlags((prev) => {
      const next = { ...prev };
      if (isUpdating) {
        next[flag] = true;
      } else {
        delete next[flag];
      }
      return next;
    });
  };

  // The prompt's flags, resolved against the CURRENT defaults. Recomputing here
  // (rather than snapshotting values when the prompt was raised) means a write
  // that lands while the modal is open pushes the value the default actually
  // holds.
  const applyPromptFlags = applyPrompt
    ? applyPrompt.flagKeys.reduce((acc, flag) => {
        acc[flag] = Boolean(companyFlags[flag]);
        return acc;
      }, {})
    : null;

  const applyPromptCopy = () => {
    if (!applyPrompt) {
      return {};
    }
    if (applyPrompt.source === "paste") {
      return {
        title: "Apply pasted defaults to locations?",
        // "matches" would overclaim: copy/paste carries the feature settings
        // only, not the Time Clock / Live Action Alerts config panels.
        description: `The company default now uses ${applyPrompt.sourceName}'s feature settings. New locations will inherit these values.`,
        dismissLabel: "Keep as defaults only",
      };
    }

    const count = applyPrompt.flagKeys.length;
    if (count === 1) {
      const flag = applyPrompt.flagKeys[0];
      const state = applyPromptFlags[flag] ? "on" : "off";
      return {
        title: `Apply ${formatFlagName(flag)} to locations?`,
        description: `${formatFlagName(flag)} is now ${state} by default. New locations start with it ${state}.`,
        dismissLabel: "Just the default",
      };
    }
    return {
      title: `Apply ${count} defaults to locations?`,
      description:
        "These defaults changed. New locations will start with these values.",
      dismissLabel: "Just the defaults",
    };
  };

  // Fold a just-written default into the outstanding prompt, keeping every flag
  // whose apply decision hasn't been made yet. A paste supersedes the framing of
  // a toggle it raced with, since it restates the whole set.
  const queueApplyPrompt = (flagKeys, source, sourceName) => {
    if ((locationData?.length || 0) === 0 || flagKeys.length === 0) {
      return;
    }

    setApplyPrompt((prev) => {
      if (!prev) {
        return { flagKeys: [...new Set(flagKeys)], source, sourceName };
      }
      const flagKeysMerged = [...new Set([...prev.flagKeys, ...flagKeys])];
      return source === "paste"
        ? { flagKeys: flagKeysMerged, source, sourceName }
        : { ...prev, flagKeys: flagKeysMerged };
    });
  };

  // Toggle a company default. Writes ONLY the company row (scope company_only),
  // then — if there are existing locations — opens the apply modal for this
  // flag. Existing locations are never changed implicitly.
  const handleCompanyDefaultToggle = async (flag) => {
    if (isFlagLocked(flag) || isAlwaysOnFlag(flag)) {
      return;
    }
    if (updatingCompanyFlags[flag]) {
      return;
    }

    const nextValue = !companyFlags[flag];

    setCompanyFlags((prev) => ({ ...prev, [flag]: nextValue }));
    setCompanyFlagUpdating(flag, true);

    const succeeded = await props.bulk_update_features?.({
      flags: { [flag]: nextValue },
      scope: "company_only",
    });

    setCompanyFlagUpdating(flag, false);

    if (succeeded) {
      queueApplyPrompt([flag], "toggle");
    } else {
      // Revert the optimistic change.
      setCompanyFlags((prev) => ({ ...prev, [flag]: !nextValue }));
    }
  };

  // Bulk "apply" targets ONLY the locations shown in this list. locationData is
  // the member-visible subset, so the modal's "All locations" option resolves to
  // those exact ids — this keeps the action in sync with the displayed count and
  // drift roll-up, and avoids scope "all" silently changing company stores the
  // admin can't see.
  const applyPromptToLocations = async (storeIds) => {
    if (!applyPromptFlags || storeIds.length === 0) {
      return false;
    }

    // Snapshot what we're pushing. A default write can resolve mid-request and
    // merge another flag into the prompt; that flag isn't in this payload, so on
    // success we retire ONLY these keys and leave the newcomer prompting.
    const pushedFlags = applyPromptFlags;
    const pushedKeys = Object.keys(pushedFlags);

    // The parent refetches locationsData (re-seeding locationData via the props
    // effect), so per-store rows and the roll-up counts update.
    const succeeded = Boolean(
      await props.bulk_update_features?.({
        flags: pushedFlags,
        scope: "stores",
        store_ids: storeIds,
      })
    );

    if (succeeded) {
      setApplyPrompt((prev) => {
        if (!prev) {
          return null;
        }
        const remaining = prev.flagKeys.filter((flag) => !pushedKeys.includes(flag));
        // Nothing left to decide on, so the prompt (and the modal) goes away.
        if (remaining.length === 0) {
          return null;
        }
        // Anything left arrived after the push, so it isn't part of whatever
        // paste framed the prompt. Fall back to neutral toggle copy rather than
        // claim these flags came from the pasted location.
        return { flagKeys: remaining, source: "toggle" };
      });
    }

    return succeeded;
  };

  // The panel's surface key maps to the backend's push surface. Time Clock and
  // Timecard Flagging edit one TimecardConfig row but push disjoint field sets,
  // so applying one never disturbs the other's settings. The clock-in window is
  // the one column both panels show; Flagging owns it. See config_fields.py.
  const CONFIG_SURFACE_API_KEY = {
    clock: "clock",
    posTimeEntries: "pos_time_entries",
    flagging: "flagging",
    liveAlerts: "live_alerts",
  };

  // Which locations a surface may be pushed to. Time Clock and Time Entries are
  // the two modes of one panel, and they share the paid_break_tracking_enabled
  // column, which means different things on each side. Pushing across modes
  // would change live paid-break behavior, so the incompatible locations are
  // never offered. The backend rejects them too. Flagging and Live Action Alerts
  // read the same in both modes, so they target everything.
  const CONFIG_SURFACE_TARGETS = {
    clock: (location) => Boolean(location.time_entries_enabled),
    posTimeEntries: (location) => !location.time_entries_enabled,
  };

  const targetsForSurface = (surfaceKey) => {
    const isEligible = CONFIG_SURFACE_TARGETS[surfaceKey];
    const all = locationData || [];
    return isEligible ? all.filter(isEligible) : all;
  };

  const configApplyLocations = configApplyPrompt
    ? targetsForSurface(configApplyPrompt.surfaceKey)
    : [];

  const applyConfigToLocations = async (storeIds) => {
    if (!configApplyPrompt || storeIds.length === 0) {
      return false;
    }

    const succeeded = Boolean(
      await props.bulk_apply_config_defaults?.({
        surface: CONFIG_SURFACE_API_KEY[configApplyPrompt.surfaceKey],
        store_ids: storeIds,
      })
    );

    if (succeeded) {
      setConfigApplyPrompt(null);
    }
    return succeeded;
  };

  // Copy the company default's flag set so it can be pasted onto locations
  // (the template made actionable). Marked sourceIsCompanyDefault so the
  // default's own paste target disables and location paste stays enabled.
  const copyCompanyDefaultSettings = () => {
    const flags = orderedFlags.reduce((acc, flag) => {
      acc[flag] = companyFlags[flag];
      return acc;
    }, {});

    setCopiedSettings({
      sourceStoreId: null,
      sourceStoreName: "Company Default",
      sourceIsCompanyDefault: true,
      flags,
    });
  };

  // Paste copied settings onto the default: write them as the company template
  // (scope company_only), then open the apply modal to push the whole set to
  // existing locations. Locked/internal-only flags are skipped so the bulk
  // endpoint doesn't reject the write for non-internal callers.
  const pasteOntoCompanyDefault = async () => {
    if (!copiedSettings || copiedSettings.sourceIsCompanyDefault || companyBusy) {
      return;
    }

    const pastedFlags = {};
    orderedFlags.forEach((flag) => {
      if (isFlagLocked(flag)) {
        return;
      }
      pastedFlags[flag] = copiedSettings.flags[flag];
    });
    ALWAYS_ENABLED_FLAGS.forEach((flag) => {
      pastedFlags[flag] = true;
    });

    if (Object.keys(pastedFlags).length === 0) {
      return;
    }

    const prevFlags = companyFlags;
    setCompanyFlags((prev) => ({ ...prev, ...pastedFlags }));
    setCompanyBusy(true);

    const succeeded = await props.bulk_update_features?.({
      flags: pastedFlags,
      scope: "company_only",
    });

    setCompanyBusy(false);

    if (succeeded) {
      queueApplyPrompt(
        Object.keys(pastedFlags),
        "paste",
        copiedSettings.sourceStoreName
      );
    } else {
      setCompanyFlags(prevFlags);
    }
  };

  // The module master's switch (or its "Always on" / locked stand-ins),
  // rendered in each bucket section header. `ctx` abstracts the flag source so
  // the same control serves both a store location and the Company Default card.
  const renderMasterControl = (ctx, bucket) => {
    const primary = bucket.primaryFlag;
    if (isAlwaysOnFlag(primary)) {
      return (
        <span className="inline-flex h-6 items-center rounded-md border border-emerald-400 bg-emerald-50 px-2 text-xs text-emerald-700">
          Always on
        </span>
      );
    }
    const locked = isFlagLocked(primary);
    const loading = ctx.isUpdating(primary);
    const checked = Boolean(ctx.flags[primary]);
    const control = (
      <div className="flex items-center space-x-2 whitespace-nowrap">
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${
            checked ? "text-emerald-600" : "text-slate-400"
          }`}
        >
          {checked ? "On" : "Off"}
        </span>
        {loading && <CircularProgress size={14} sx={{ color: "#64748b" }} />}
        <PurpleSwitch
          inputProps={{ "aria-label": `${bucket.title} enabled` }}
          checked={checked}
          onClick={() => ctx.onToggleFlag(primary)}
          disabled={loading || locked}
        />
      </div>
    );
    if (locked) {
      return (
        <Tooltip title={getLockedFlagMessage(primary)}>
          <span className="inline-flex cursor-help">{control}</span>
        </Tooltip>
      );
    }
    return control;
  };

  // Compact child row for the module cards: name + switch, with the
  // description on a hover tooltip.
  // Marks a module or sub-setting that differs from the company default, so the
  // drift count on the location header points somewhere.
  const renderDriftBadge = (label) => (
    <span className="shrink-0 whitespace-nowrap rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
      {label}
    </span>
  );

  const renderCompactFlagRow = ({
    flag,
    checked,
    onToggle,
    loading = false,
    meta = null,
    drifted = false,
    id,
  }) => {
    const locked = isFlagLocked(flag);
    const description = locked ? getLockedFlagMessage(flag) : getFlagTooltip(flag);
    return (
      <div key={flag} id={id} className="py-2.5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className="flex flex-wrap items-center gap-1.5">
              <span
                className={`text-sm font-medium ${
                  locked ? "text-slate-500" : "text-slate-800"
                }`}
              >
                {formatFlagName(flag)}
              </span>
              {drifted && renderDriftBadge("Differs")}
            </span>
            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                {description}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-1.5 whitespace-nowrap pt-0.5">
            {loading && <CircularProgress size={12} sx={{ color: "#64748b" }} />}
            <PurpleSwitch
              inputProps={{ "aria-label": formatFlagName(flag) }}
              checked={Boolean(checked)}
              onClick={onToggle}
              disabled={loading || locked}
            />
          </div>
        </div>
        {meta}
      </div>
    );
  };

  const renderFeatureEditors = (panel, flags, active = true) => <FeatureSettings
    active={active}
    flat editorId={panel.bucketKey === "location_details" ? "hours" : panel.scope === "company" ? panel.focusEditorId : undefined} activeEditorId={panel.scope !== "company" ? panel.focusEditorId : undefined} hideHeading={panel.scope === "company"} scopeFilter={panel.scope === "company" ? "company" : "location"}
    bucketKey={panel.bucketKey} flags={flags}
    isCompanyDefault={panel.scope === "defaults"}
    companyId={props.companyId} storeNames={props.storeNames}
    onDirtyChange={props.onFeatureSettingsDirtyChange} onSaved={props.refreshMemberStores}
    renderLocationEditor={props.renderLocationEditor} permissionRole={props.permissionRole}
    opsRefreshSignal={props.opsRefreshSignal}
    focusEditor={panel.focusEditorId && panel.scope !== "company" && selection?.scope === panel.scope && selection?.storeId === panel.storeId && selection?.bucketKey === panel.bucketKey ? { editorId: panel.focusEditorId, at: selection } : Number(props.focusEditor?.storeId) === Number(flags.id) ? props.focusEditor : undefined}
    onOpenLaborModel={props.onOpenLaborModel}
  />;

  // Availability of shared editors follows live locations, not the template.
  const liveCompanyFlags = Object.fromEntries(orderedFlags.map((flag) => [flag,
    (locationData || []).some((location) => Boolean(location[flag]))
  ]));

  const renderDetailPanel = (panel) => {
    const isDefault = panel.scope === "defaults";
    const isShared = panel.scope === "company";
    const location = locationData?.find((item) => Number(item.id) === Number(panel.storeId));
    const flags = isShared ? liveCompanyFlags : isDefault ? companyFlags : location;
    if (!flags) return <p className="p-6 text-sm text-slate-500">This location is no longer available.</p>;
    const bucket = visibleBuckets.find((item) => item.key === panel.bucketKey);
    const ctx = {
      flags, isCompanyDefault: isDefault,
      onToggleFlag: isDefault ? handleCompanyDefaultToggle : (flag) => handleInlineFeatureToggle(location, flag),
      isUpdating: isDefault ? (flag) => companyBusy || isCompanyFlagUpdating(flag) : (flag) => isStoreUpdating(location) || isFlagUpdating(location, flag),
    };
    return <div className={`px-6 pb-8 sm:px-4 ${isShared ? "pt-5" : ""}`}>
      {(isShared || isDefault) && <p className="mb-5 text-xs leading-relaxed text-slate-500">{isShared
        ? getCompanyEditor(panel)?.scopeHelp || "Company-wide settings · applies to all locations"
        : "New locations start with these defaults. Changes to existing locations require an explicit apply."}</p>}
      {!isShared && bucket && <>
        <div hidden={Boolean(panel.focusEditorId || panel.focusSurface)}>
        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-semibold leading-tight text-slate-900">{bucket.title}</h3>
              {FEATURE_ACCESS[bucket.primaryFlag] && <div className="mt-2"><FeatureAccessBadge access={FEATURE_ACCESS[bucket.primaryFlag]} /></div>}
            </div>
            <div className="shrink-0">{renderMasterControl(ctx, bucket)}</div>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-slate-600">{getFlagTooltip(bucket.primaryFlag)}</p>
        </div>
        {FEATURE_ACCESS[bucket.primaryFlag] && <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">{isRightworkInternal
          ? "Only Rightwork can change this feature. Customers can view its status and access details."
          : FEATURE_ACCESS[bucket.primaryFlag].message}</p>}
        {bucket.requiresScheduling && !flags.scheduling_enabled && <p className="mb-3 text-sm text-amber-700">Requires Scheduling to be enabled.</p>}
        {!flags[bucket.primaryFlag] && bucket.childFlags.length > 0 && <p className="mb-3 text-xs text-slate-500">The switches below take effect when {bucket.title} is enabled.</p>}
        {bucket.childFlags.length > 0 && <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Feature options</h4>}
        <div className="divide-y divide-slate-100">
          {bucket.childFlags.map((flag) => renderCompactFlagRow({
            id: `${panel.scope}:${panel.storeId || "company"}:${panel.bucketKey}:${flag}`, flag, checked: flags[flag], onToggle: () => ctx.onToggleFlag(flag), loading: ctx.isUpdating(flag),
            drifted: !isDefault && Object.keys(companyFlags).length > 0 && Boolean(flags[flag]) !== Boolean(companyFlags[flag]),
          }))}
        </div>
        </div>
        <div hidden={Boolean(panel.focusEditorId)}>{selection?.key === `${panel.scope}:${panel.storeId || "company"}:${panel.bucketKey}` && renderConfigSurfaces(ctx, bucket)}</div>
      </>}
      {!isShared && !isDefault && panel.bucketKey === "location_details" && <LocationDetailsEditor
        key={location.id} location={location} companyId={props.companyId} groupOptions={groupOptions}
        isRightworkInternal={isRightworkInternal} canEditContactDetails={Boolean(props.canEditContactDetails)} onDirtyChange={props.onFeatureSettingsDirtyChange}
        onSave={(data, options) => props.update_store_info(applyAlwaysEnabledFlags(data), options)}
      />}
      <div hidden={Boolean(panel.focusSurface)}>{renderFeatureEditors(panel, flags)}</div>
    </div>;
  };

  const renderConfigSurfaces = (ctx, bucket) => {
    const masterOn = Boolean(ctx.flags[bucket.primaryFlag]);
    const surfaces = bucket.configSurfaces || [];
    if (bucket.key !== "time_attendance") return surfaces.map((surface) => renderConfigSurfaceRow(ctx, surface, masterOn));
    const enabled = surfaces.filter((surface) => surface.isEnabled(ctx.flags, masterOn));
    // Clock and flagging edit the same config record. Render the existing full
    // editor once so shared fields cannot display conflicting local snapshots.
    return renderConfigSurfaceRow(ctx, {
      key: "timecard", label: "Time clock & timecard rules",
      description: "Clock-in requirements, breaks, and timecard exceptions.",
      isEnabled: () => enabled.length > 0,
      section: enabled.length > 1 ? undefined : enabled[0]?.section || enabled[0]?.key,
      applySurfaces: enabled,
    }, masterOn);
  };

  // A config panel entry (Time Clock / Timecard Flagging / Live Action Alerts).
  // Both the Company Default card and each location row expand the same panel
  // inline; the panel auto-saves every change. In company scope it edits the
  // TEMPLATE new locations inherit, so it also offers an explicit push.
  const renderConfigSurfaceRow = (ctx, surface, masterOn) => {
    // Reachability is the surface's own call, because it has to agree with what
    // the panel renders (see featureFlagConstants). A surface with no predicate
    // just follows its module master.
    const surfaceEnabled = surface.isEnabled
      ? Boolean(surface.isEnabled(ctx.flags, masterOn))
      : masterOn;

    // A disabled surface normally stays visible but muted, so an admin can see
    // what turning the module on would unlock. That reads as noise for a surface
    // whose only route in is switching a mode off (Time Clock vs Time Entries):
    // drop it instead.
    if (!surfaceEnabled && surface.hideWhenDisabled) {
      return null;
    }

    const isCompany = Boolean(ctx.isCompanyDefault);
    const description =
      (isCompany && surface.companyDescription) || surface.description;

    const label = (
      <div className="min-w-0">
        <span className="text-sm font-medium text-slate-800">{surface.label}</span>
        {description && (
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
            {description}
          </p>
        )}
      </div>
    );

    const location = isCompany ? null : ctx.flags;
    return (
      <section key={surface.key} data-config-surface={surface.key} className="scroll-mt-24 mt-6 border-t border-slate-200 pt-4">
        <div className="mb-4">{label}</div>
        {!surfaceEnabled && <p className="text-xs text-slate-500">Enable this feature to configure these settings.</p>}
        {surfaceEnabled && (
          <div>
            <div className="pb-2">
              {/* These panels auto-save, so the push can't be a modal per
                  keystroke like the flag toggles. One button per panel pushes
                  the whole surface. */}
              {isCompany && <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
                {(surface.applySurfaces || [surface]).filter((item) => targetsForSurface(item.key).length > 0).map((item) => (
                <div key={item.key}>
                  <button
                    type="button"
                    className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                    // configSaving: the push reads the template from the server,
                    // so a pending auto-save must land first or we'd copy out the
                    // pre-edit values. companyFlagWriteInFlight: that write opens
                    // its own apply modal when it lands, so the two can't stack.
                    disabled={
                      configSaving || companyBusy || companyFlagWriteInFlight
                    }
                    onClick={() =>
                      setConfigApplyPrompt({
                        surfaceKey: item.key,
                        label: item.label,
                      })
                    }
                  >
                    Apply {item.label} to locations
                  </button>
                </div>
                ))}
              </div>}
              {surface.key === "liveAlerts" ? (
                <LiveAlertSettings
                  inline
                  autoSave
                  companyScope={isCompany}
                  companyId={isCompany ? props.companyId : undefined}
                  homeStore={isCompany ? undefined : location.id}
                  onSavingChange={isCompany ? (busy) => reportConfigSaving(surface.key, busy) : undefined}
                />
              ) : (
                <TimeEntriesSettings
                  inline
                  autoSave
                  embedded
                  section={surface.key === "timecard" ? surface.section : surface.section || surface.key}
                  companyScope={isCompany}
                  companyId={isCompany ? props.companyId : undefined}
                  homeStore={isCompany ? undefined : location.id}
                  timeEntriesEnabled={ctx.flags.time_entries_enabled}
                  onSavingChange={isCompany ? (busy) => reportConfigSaving(surface.key, busy) : undefined}
                />
              )}
            </div>
          </div>
        )}
      </section>
    );
  };

  function handleSubmit() {
    const latestStoreData = modalEdit
      ? locationData?.find((location) => isMatchingLocation(location, modalData))
      : null;

    const dataToSubmit = latestStoreData
      ? {
          ...latestStoreData,
          store_name: modalData.store_name,
          latitude: modalData.latitude,
          longitude: modalData.longitude,
          onboarding_status: modalData.onboarding_status,
          store_group: modalData.store_group ?? null
        }
      : modalData;

    const normalizedDataToSubmit = applyAlwaysEnabledFlags(dataToSubmit);

    // The drawer stays open when the save is refused (the parent verifies a
    // store-group change against the refreshed data before saying so), and
    // the button is disabled meanwhile so a second click cannot create a
    // duplicate location.
    setModalSaving(true);
    Promise.resolve(props.update_store_info(normalizedDataToSubmit))
      .then((ok) => { if (ok !== false) handleClose(); })
      .finally(() => setModalSaving(false));
  }


  const hasUnimportedPosStores = props.posStores?.length > 0;

  return (
    <>

      <div hidden={showingCompanyPage} className="mx-6 py-5 sm:mx-3">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Features & settings</h2>
            <p className="mt-1 text-xs text-slate-500">Select a cell to edit. Expand a feature to see its switches and additional settings.</p>
          </div>
          <button type="button" className="flex items-center gap-1 rounded-lg bg-violet-700 px-3 py-2 text-sm font-medium text-white hover:bg-violet-800"
            onClick={() => { setModalData(default_data); setModalEdit(false); handleOpen(); }}>
            <AddIcon fontSize="small" /> Add location
          </button>
        </div>
        {hasUnimportedPosStores && <div className="mb-3 flex items-center gap-2 text-sm text-amber-700">
          <WarningIcon fontSize="small" /> {props.posStores.length} locations not imported from POS
          <button type="button" className="font-medium underline" onClick={handlePosModalOpen}>Review</button>
        </div>}
        {copiedSettings && <div className="mb-3 flex items-center justify-between gap-3 text-xs text-slate-600" role="status">
          <span>Feature switches copied from {copiedSettings.sourceStoreName}. Select a destination to paste.</span>
          <button type="button" className="font-medium text-violet-700" onClick={() => setCopiedSettings(null)}>Clear</button>
        </div>}
        <FeatureComparison key={props.companyId} buckets={visibleBuckets} locations={locationData || []} companyFlags={companyFlags}
          permissionRole={props.permissionRole} onSelect={selectSettings} selection={selection} isRightworkInternal={isRightworkInternal} />
      </div>
      {Object.entries(visitedPanels).filter(([, panel]) => isCompanyPage(panel)).map(([key, panel]) => <section key={key} hidden={selection?.key !== key} className="px-6 py-5 sm:px-3" aria-label={getCompanyEditor(panel).title}>
        <button type="button" onClick={returnToComparison} className="mb-4 flex items-center gap-2 rounded text-sm font-medium text-violet-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">
          <ArrowBackIcon fontSize="small" />Back to settings
        </button>
        <h2 className="text-xl font-semibold text-slate-900">{getCompanyEditor(panel).title}</h2>
        <p className="mt-2 mb-5 text-sm text-slate-500">{getCompanyEditor(panel).scopeHelp}</p>
        <div className="rounded-xl border border-slate-200 bg-white p-5 sm:p-3">{renderFeatureEditors(panel, liveCompanyFlags, selection?.key === key && props.settingsActive !== false)}</div>
      </section>)}
      <Drawer anchor="right" open={Boolean(selection) && !showingCompanyPage && props.settingsActive !== false} onClose={() => setSelection(null)} ModalProps={{ keepMounted: true }}
        PaperProps={{ ref: detailPaper, sx: { width: 640, maxWidth: "100vw" } }}>
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-100 bg-white px-6 py-4 sm:px-4">
          <h2 className="text-xl font-semibold text-slate-900">{getCompanyEditor(selection)?.title || "Feature settings"}</h2>
          <div className="flex items-center gap-2">
            {selection && selection.scope !== "company" && (() => {
              const isDefault = selection.scope === "defaults";
              const location = locationData?.find((item) => Number(item.id) === Number(selection.storeId));
              if (!isDefault && !location) return null;
              return <FeatureSettingsActions isDefault={isDefault}
                copyDisabled={isDefault ? companyBusy || companyFlagWriteInFlight : isStoreUpdating(location)}
                pasteDisabled={!copiedSettings || (isDefault ? copiedSettings.sourceIsCompanyDefault || companyBusy || companyFlagWriteInFlight : copiedSettings.sourceStoreId === location.id || isStoreUpdating(location))}
                copiedSource={copiedSettings?.sourceStoreName}
                onCopy={() => isDefault ? copyCompanyDefaultSettings() : copyStoreSettings(location)}
                onPaste={() => isDefault ? pasteOntoCompanyDefault() : pasteStoreSettings(location)} />;
            })()}
          <button type="button" aria-label="Close feature settings" onClick={() => setSelection(null)} className="rounded p-2 text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600"><CloseIcon /></button>
          </div>
        </div>
        {selection && selection.scope !== "company" && <div className="grid grid-cols-2 gap-3 px-6 py-4 sm:grid-cols-1 sm:px-4">
          {selection.scope !== "company" && <div className="min-w-0">
            <label id="comparison-scope-label" className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="comparison-scope">Location or defaults</label>
            <RightworkSelect id="comparison-scope" value={selection.scope === "defaults" ? "defaults" : selection.storeId}
              onChange={(event) => selectSettings({ scope: event.target.value === "defaults" ? "defaults" : "location", storeId: event.target.value === "defaults" ? undefined : Number(event.target.value), bucketKey: selection.bucketKey === "location_details" && event.target.value === "defaults" ? "scheduling" : selection.bucketKey })}
              options={[...(Object.keys(companyFlags).length > 0 ? [{ value: "defaults", label: "Company defaults · new locations" }] : []),
                ...(locationData || []).map((location) => ({ value: location.id, label: location.store_name }))]} />
          </div>}
          <div className="min-w-0">
            <label id="comparison-feature-label" className="mb-1 block text-xs font-semibold text-slate-600" htmlFor="comparison-feature">Feature or settings</label>
            <RightworkSelect id="comparison-feature" value={selection.bucketKey} onChange={(event) => selectSettings({ ...selection, bucketKey: event.target.value, focusFlag: undefined, focusEditorId: undefined })}
              options={[...(selection.scope === "location" ? [{ value: "location_details", label: "Location details & hours" }] : []),
                ...visibleBuckets.map((bucket) => ({ value: bucket.key, label: bucket.title }))]} />
            </div>
        </div>}
        {Object.entries(visitedPanels).filter(([, panel]) => !isCompanyPage(panel)).map(([key, panel]) => <div key={key} hidden={selection?.key !== key}>{renderDetailPanel(panel)}</div>)}
      </Drawer>

      {/* Where to push a just-written company default. */}
      <ApplyDefaultsToLocationsModal
        open={Boolean(applyPrompt)}
        onClose={() => setApplyPrompt(null)}
        onApply={applyPromptToLocations}
        flags={applyPromptFlags}
        locations={locationData}
        {...applyPromptCopy()}
      />

      {/* Where to push a company config panel's settings. */}
      <ApplyDefaultsToLocationsModal
        open={Boolean(configApplyPrompt)}
        onClose={() => setConfigApplyPrompt(null)}
        onApply={applyConfigToLocations}
        locations={configApplyLocations}
        summary={
          // Not "All {label} settings": the live-alert surface is itself
          // labelled "Alert settings", which would double the word. The second
          // sentence explains why the list can be shorter than the location
          // count, which would otherwise read as a bug.
          configApplyPrompt
            ? `Every setting in ${configApplyPrompt.label} will be copied to the locations you pick, overwriting theirs.` +
              (CONFIG_SURFACE_TARGETS[configApplyPrompt.surfaceKey]
                ? " Only locations that keep time the same way are listed."
                : "")
            : undefined
        }
        title={
          configApplyPrompt
            ? `Apply ${configApplyPrompt.label} to locations?`
            : undefined
        }
        // Scoped deliberately to flagging rules. The flagging surface excludes
        // every labor-law field, and a labor-law location derives its flagging
        // from its jurisdiction regardless. A broader "nothing labor law
        // touches is overwritten" would be false: the Time Clock surface does
        // push min_break_duration_minutes, which the jurisdiction sync also
        // writes. That only affects clock-side break enforcement.
        description="Timecard flagging rules that labor law sets for a location are not changed."
        dismissLabel="Cancel"
      />

      {/* Import Stores Modal */}
      <ImportStoresModal
        open={posModalOpen}
        onClose={handlePosModalClose}
        posStores={props.posStores}
        existingStores={locationData}
        link_or_create_store_from_pos={props.link_or_create_store_from_pos}
        archive_pos_store={props.archive_pos_store}
      />

      {/* Edit-location Drawer */}
      <Drawer open={open} anchor="right" onClose={handleClose}>
        <div className="w-[450px] max-w-full h-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title={
                modalEdit ? `Edit ${modalData.store_name}` : "Add Location"
              }
              onClose={handleClose}
            />
            <div className="w-full px-6">
              {!modalEdit && (
                <div className="mt-5">
                  <p className="text-slate-500 text-base mt-1">
                    Create the location first, then configure or copy settings from the list.
                  </p>
                </div>
              )}
              {modalEdit && (
                <div className="mt-5">
                  <p className="text-slate-500 text-base mt-1">
                    Update the location name, coordinates, and status here. Select a feature in the comparison to manage its settings.
                  </p>
                </div>
              )}
              {["store_name", "latitude", "longitude"].map((field) => (
                <div key={field} className="mt-5">
                  <p className="text-sm text-slate-500 capitalize">{field.replace("_", " ")}</p>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      name={field}
                      value={modalData[field] || ""}
                      onChange={handleChange}
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "8px",
                          padding: "5px",
                          height: "45px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              ))}
              {modalEdit && (
                <div className="mt-5">
                  <label id="location-district-label" htmlFor="location-district" className="text-sm text-slate-500">Store group</label>
                  <div className="mt-1">
                    <RightworkSelect
                      id="location-district"
                      value={modalData.store_group ?? ""}
                      onChange={(e) => setModalData((prev) => ({ ...prev, store_group: e.target.value === "" ? null : e.target.value }))}
                      options={[
                        { value: "", label: "No group" },
                        ...groupOptions,
                      ]}
                    />
                  </div>
                  {groupOptions.length === 0 && <p className="mt-2 text-sm text-slate-500">Add regions or districts under Settings &gt; Store groups.</p>}
                </div>
              )}
              {!isRightworkInternal && (
                <div className="mt-5">
                  <p className="text-sm text-slate-500">Billing status</p>
                  <p className="mt-1 text-sm text-slate-700">
                    {getFormattedOnboardingStatus[modalData.onboarding_status] ||
                      modalData.onboarding_status ||
                      "Setting up"}
                  </p>
                  <p className="mt-2 text-sm text-slate-500">
                    Rightwork activates a location once setup is complete. To request
                    a change, use the Billing page or ask the setup assistant.
                  </p>
                </div>
              )}
              {isRightworkInternal && (
              <div className="mt-5">
                <p className="text-sm text-slate-500">Billing status</p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    size="small"
                    name="onboarding_status"
                    value={modalData.onboarding_status || "setting_up"}
                    onChange={handleChange}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      height: "45px",
                      "& .MuiSelect-select": {
                        padding: "10px 16px",
                        minHeight: "unset",
                        display: "flex",
                        alignItems: "center",
                        height: "45px",
                        boxSizing: "border-box",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    }}
                  >
                    <MenuItem value="setting_up">Setting up</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  Only active stores are billed. Changing this will affect the next billing cycle.
                </p>
              </div>
              )}
            </div>
            <div className="h-[200px]" />
          </div>
          <DrawerFooterContainerLarge>
            <div className="w-full">
              <LoadingButton
                variant="contained"
                loading={modalSaving}
                loadingPosition="end"
                endIcon={<SaveIcon />}
                onClick={handleSubmit}
                sx={{
                  width: "100%",
                  textTransform: "none",
                  borderRadius: "12px",
                  paddingY: "14px",
                  backgroundColor: "#6d28d9",
                  "&:hover": {
                    backgroundColor: "#5b21b6",
                  },
                  fontSize: "16px",
                }}
              >
                {modalEdit ? "Save Changes" : "Save New Location"}
              </LoadingButton>
            </div>
            <div className="w-full mt-3">
              <ButtonOutlined text="Cancel" onClick={handleClose} />
            </div>
          </DrawerFooterContainerLarge>
        </div>
      </Drawer>
    </>
  );
}

export default ManageLocationsDisplay;
